/**
 * The authenticated fetch wrapper both clients use.
 *
 * The two clients authenticate differently, and not by preference — Authgate
 * forces it. The access token is delivered as an **HttpOnly cookie**, so
 * browser code can never read it:
 *
 *   > A body would mean the SPA holds the token in JavaScript, where any XSS
 *   > can read it — and at that point HttpOnly protects nothing.
 *   >   — auth-service, SessionCookies
 *
 * So:
 *
 *   Web    — sends no token. The browser attaches the access cookie
 *            (`__Host-ag_session`; `ag_session` over plain HTTP) automatically.
 *            Authgate's cookies are host-only, so the API, `/auth` and
 *            `/session` are all served on the app's own origin. Refresh is a
 *            credentialed POST to `/session/refresh` there; the app never sees
 *            either token.
 *   Native — the same adapter, against the same origin. React Native's `fetch`
 *            is backed by NSURLSession and OkHttp, both of which keep a
 *            persistent app-private cookie jar, so the phone can hold a cookie
 *            session too.
 *
 * There is deliberately no bearer-token adapter. The API would accept one —
 * `app/core/security.py` takes an `Authorization` header as readily as the
 * cookie — but Authgate has no endpoint that hands a token to a client that
 * can read it. `/auth/code/verify` answers with `Set-Cookie` and a bare
 * `{expiresInSeconds}`, and `/session/refresh` reads the refresh token from a
 * cookie and ignores the request body. An adapter that posted `{refresh_token}` would
 * be answered with a 401 every time, so shipping one would only describe a
 * capability this system does not have.
 */

export interface AuthAdapter {
  /** Header to send, if this client has a token it can read. */
  authorization(): Promise<string | null>;
  /** Whether the browser should attach cookies to the request. */
  readonly credentials: RequestCredentials;
  /** Renew the session. False means it's genuinely over — sign in again. */
  refresh(): Promise<boolean>;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly detail: string,
  ) {
    super(detail);
  }
}

/** Thrown when refresh failed — the caller should route to sign-in. */
export class SessionExpired extends ApiError {}

/**
 * Cookie auth — the browser, and the phone.
 *
 * There is no token to read or store: `authorization()` is always null and the
 * cookie does the work. `X-Calorio-Client` is the CSRF gate — the API refuses
 * cookie-authenticated requests without it, because a cross-site form can make
 * the browser attach a cookie but cannot set a header.
 */
/**
 * `origin` is where Authgate's `/session/*` is served — the app's own origin,
 * never `auth.dakir.app`: the cookies are host-only, so a refresh anywhere else
 * neither sends this app's refresh cookie nor sets an access cookie the API
 * will receive. `""` means "the page's own origin", which is what the web uses.
 */
export function createCookieAuth(origin: string): AuthAdapter {
  return {
    credentials: "include",
    async authorization() {
      return null;
    },
    async refresh() {
      const response = await fetch(`${origin}/session/refresh`, {
        method: "POST",
        credentials: "include",
      });
      // Authgate replies { expiresInSeconds } and re-sets the cookies. The body
      // is of no use to us; the Set-Cookie is the whole point.
      return response.ok;
    },
  };
}

export function createClient(baseUrl: string, auth: AuthAdapter) {
  // Refresh is single-flight, and this is not an optimisation.
  //
  // A page load fires several queries at once. When the access token has
  // expired they all get 401 together, and without this they would each
  // refresh — presenting the *same* refresh token concurrently. Authgate
  // rotates on every refresh, so exactly one of those wins and the rest arrive
  // to find the token spent. It has a grace window that stops it reading the
  // race as theft, but the losers are still answered with a failure:
  //
  //   > A browser with several calls in flight answers one expiry with several
  //   > refreshes of the SAME cookie. One wins; the others arrive moments later
  //   > to find it spent [...]
  //   >   — auth-service, SessionService.refresh
  //
  // So the losers would raise SessionExpired and bounce the user to sign-in
  // immediately after a refresh that actually succeeded. On native it is worse:
  // each loser calls store.clear(), erasing the tokens the winner just saved.
  //
  // Sharing one in-flight promise means N concurrent 401s produce one refresh
  // and N retries. The server tolerates the race; the client should not create
  // it.
  let inFlight: Promise<boolean> | null = null;
  const refreshOnce = () => {
    inFlight ??= auth.refresh().finally(() => {
      inFlight = null;
    });
    return inFlight;
  };

  async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const send = async () => {
      const authorization = await auth.authorization();
      return fetch(`${baseUrl}${path}`, {
        method,
        credentials: auth.credentials,
        headers: {
          // Required on the cookie path and harmless on the bearer one, so it
          // goes on every request rather than being conditional.
          "X-Calorio-Client": "calorio",
          ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
          ...(authorization ? { Authorization: authorization } : {}),
        },
        body: body !== undefined ? JSON.stringify(body) : undefined,
      });
    };

    let response = await send();

    // One retry on 401, and only one: a second failure means the refresh token
    // is dead too, and retrying past that builds an infinite loop against your
    // own auth service.
    if (response.status === 401) {
      if (!(await refreshOnce())) {
        throw new SessionExpired(401, "your session has expired");
      }
      response = await send();
      if (response.status === 401) {
        throw new SessionExpired(401, "your session has expired");
      }
    }

    if (response.status === 204) return undefined as T;

    if (!response.ok) {
      const detail = await response
        .json()
        .then((b) => b?.detail ?? response.statusText)
        .catch(() => response.statusText);
      throw new ApiError(response.status, String(detail));
    }

    return response.json() as Promise<T>;
  }

  return {
    get: <T>(path: string) => request<T>("GET", path),
    post: <T>(path: string, body: unknown) => request<T>("POST", path, body),
    put: <T>(path: string, body: unknown) => request<T>("PUT", path, body),
    patch: <T>(path: string, body: unknown) => request<T>("PATCH", path, body),
    delete: <T>(path: string) => request<T>("DELETE", path),
  };
}

export type Api = ReturnType<typeof createClient>;
